import { tilePoster } from "../library/posterSize.js";
import { useQueryClient } from "@tanstack/react-query";
import { alternateStreamUrls } from "../library/streamAlternates.js";
import { useProgressiveList } from "../library/useProgressiveList.js";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import type { VodItem } from "@hybrid-iptv/types";
import { cn, EmptyState, ErrorState, Section, Spinner } from "@hybrid-iptv/ui";
import { ProviderSetupPrompt, isProviderNotConfigured } from "../components/ProviderSetupPrompt.js";
import {
  tmdbImageUrl,
  useMovieMatch,
  useProviderMovieDetails,
  useTmdbDetails,
} from "../library/vodHooks.js";
import { movieTitleFields } from "../library/titleFields.js";
import { popularSortAvailable, useOpenRelated } from "../library/relatedTitles.js";
import { useCastPhotos } from "../library/castPhotos.js";
import { usesTmdb } from "../discovery/rowSupplier.js";
import { useInView } from "../library/useInView.js";
import { TitleDetail } from "../library/TitleDetail.js";
import { formatRemaining, isResumable, useProgressStore } from "../library/progressStore.js";
import { useActiveProviderSourceId } from "../epg/api.js";
import { usePlayerStore } from "../player/playerStore.js";
import { FavoriteStar } from "../library/FavoriteStar.js";
import { FAVORITES_CATEGORY } from "../library/favoritesStore.js";
import { useFavoriteRefs } from "../library/useFavorites.js";
import { genreLabel, orderMovieGenres } from "../library/genreOrder.js";
import { useViewerCountry } from "../settings/countryStore.js";
import { useCatalogGenres, useCatalogQuery } from "../library/useCatalogQuery.js";
import type { CatalogQuery } from "../library/catalogQuery.js";
import { useSettled } from "../library/useSettled.js";
import { tileLetter } from "../library/tileLetter.js";

type SortKey = "az" | "year" | "recent" | "popular";

/** Render cap — a provider catalog can be tens of thousands of titles. */
const DISPLAY_CAP = 300;

export function MoviesPage() {
  const queryClient = useQueryClient();
  const sourceId = useActiveProviderSourceId();
  // Search hands off a title here as `/movies?q=…` (from Home's My List rail
  // and SearchPage); pre-fill the box so the grid is already narrowed.
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  // Open on the device: most popular first (owner, 2026-10-11), from the discovery files.
  const popularOrder = popularSortAvailable();
  const [sort, setSort] = useState<SortKey>(popularOrder ? "popular" : "az");
  const [genre, setGenre] = useState("all");
  const [selected, setSelected] = useState<VodItem | null>(null);

  const favoriteMovieRefs = useFavoriteRefs("movie");
  // Stage B: the catalog is queried, not held. In the APK native SQLite answers;
  // on the website the same query runs over the catalog the server sends.
  const genreList = useCatalogGenres(sourceId, "vod");
  const genres = genreList.data ?? [];
  // All genres, then Favorites, then the new-movie categories (newest year
  // first), then the rest A to Z (owner, 2026-10-06).
  const country = useViewerCountry();
  const orderedGenres = useMemo(() => orderMovieGenres(genres, country), [genres, country]);
  // The grid follows the PAUSE in typing, not the keystroke: each letter
  // otherwise re-queried the catalog and re-rendered 300 tiles (measured on a
  // phone, 62-73ms a keystroke before the results even arrived).
  const settledQuery = useSettled(query, 200);
  const catalogQuery = useMemo<CatalogQuery>(
    () => ({
      kind: "vod",
      ...(genre === FAVORITES_CATEGORY ? { ids: favoriteMovieRefs } : genre !== "all" ? { category: genre } : {}),
      ...(settledQuery ? { text: settledQuery } : {}),
      sort: sort === "recent" ? "pos" : sort,
      limit: DISPLAY_CAP,
    }),
    [genre, favoriteMovieRefs, settledQuery, sort],
  );
  const page = useCatalogQuery<VodItem>(sourceId, catalogQuery);
  const isLoading = page.isLoading || genreList.isLoading;
  const isError = page.isError || genreList.isError;
  const error = page.error ?? genreList.error;
  const total = page.data?.total ?? 0;
  const all = page.data?.items ?? [];
  // Painted a batch at a time on a TV (see useProgressiveList).
  const shown = useProgressiveList(all);

  // Detail overlay data (hooks are unconditional; empty title is a no-op query).
  const openTitle = useCallback((movie: VodItem) => setSelected(movie), []);
  const match = useMovieMatch(selected?.rawTitle ?? "", selected?.tmdbId);
  const details = useTmdbDetails("movie", match.data?.match?.tmdbId);
  // The panel's own details first (Section 26); TMDB fills what it left out.
  const providerDetails = useProviderMovieDetails(selected);
  const fields = movieTitleFields(selected, providerDetails.data, details.data, match.data?.match?.summary);
  const progress = useProgressStore((s) => (selected ? s.get(selected.id) : undefined));

  // OK on a "You May Also Like" / "Similar Titles" poster opens that movie's
  // details when it is in the playlist (owner, 2026-10-06). The posters are
  // looked up together, by the provider's TMDB ids, as the details load. Each
  // step remembers where it came from: Back (or Close) goes to the movie before,
  // onto the poster that was picked, and from the first movie closes the panel.
  const [trail, setTrail] = useState<Array<{ item: VodItem; via: number }>>([]);
  const returnToPoster = useRef<number | null>(null);
  const relatedIds = useMemo(
    () => [...(details.data?.recommendations?.results ?? []), ...(details.data?.similar?.results ?? [])].map((r) => r.id),
    [details.data],
  );
  const relatedQuery = useMemo<CatalogQuery>(() => ({ kind: "vod", tmdbIds: relatedIds, limit: 40 }), [relatedIds]);
  const relatedPage = useCatalogQuery<VodItem>(sourceId, relatedQuery, selected !== null && relatedIds.length > 0);
  const inPlaylist = useMemo(() => {
    const byTmdb = new Map<number, VodItem>();
    for (const item of relatedPage.data?.items ?? []) if (item.tmdbId && !byTmdb.has(item.tmdbId)) byTmdb.set(item.tmdbId, item);
    return byTmdb;
  }, [relatedPage.data]);
  // Open (Section 26): Similar and You May Also Like from the discovery files, playable titles only.
  const open = useOpenRelated<VodItem>(sourceId, "vod", selected, !usesTmdb());
  // Open: Commons photos for the cast (Section 26 step 7), initials where there is none.
  const cast = useCastPhotos(sourceId, "vod", selected, fields.cast, !usesTmdb());
  const openRelated = (tmdbId: number): boolean => {
    const item = inPlaylist.get(tmdbId) ?? open.byTmdb.get(tmdbId);
    if (!item || !selected) return false;
    setTrail((steps) => [...steps, { item: selected, via: tmdbId }]);
    setSelected(item);
    return true;
  };
  const closeDetail = () => {
    const last = trail.at(-1);
    if (!last) {
      setSelected(null);
      return;
    }
    setTrail(trail.slice(0, -1));
    returnToPoster.current = last.via;
    setSelected(last.item);
  };
  // Back on the movie before: focus onto the poster that opened the next one,
  // once its details (and the poster rows) have drawn again.
  useEffect(() => {
    const id = returnToPoster.current;
    if (id === null || !selected) return;
    let tries = 0;
    const timer = setInterval(() => {
      const poster = document.querySelector<HTMLElement>(`[data-related-tmdb="${id}"]`);
      if (poster || ++tries > 40) {
        clearInterval(timer);
        returnToPoster.current = null;
        poster?.focus();
        poster?.scrollIntoView({ block: "nearest", inline: "nearest" });
      }
    }, 100);
    return () => clearInterval(timer);
  }, [selected, details.data]);
  const resumable = isResumable(progress);

  async function handlePlay(item: VodItem) {
    const saved = useProgressStore.getState().get(item.id);
    const store = usePlayerStore.getState();
    store.setPlayingContent({ contentType: "vod", contentRef: item.id });
    useProgressStore.getState().remember(item.id, { title: item.rawTitle, posterUrl: item.posterUrl, url: item.streamUrl });
    // Close the detail overlay BEFORE awaiting playback so the bottom-dock
    // player is visible immediately — even while the stream is still loading
    // (or if it fails), the user should see the player, not a modal over it.
    setSelected(null);
    setTrail([]);
    // TMDB runtime (minutes) is the only length a transcoded VOD can report: it
    // is a live-shaped pipe, so video.duration is null and the seek bar would
    // otherwise have no scale. Approximate, but a real fixed number.
    const runtimeSeconds = fields.runtimeMinutes ? fields.runtimeMinutes * 60 : undefined;
    // Other streams of this same film, tried if this one will not play (duplicates.ts).
    const alternates = await alternateStreamUrls(queryClient, item);
    await store.playSource({
      url: item.streamUrl,
      type: "vod",
      title: item.rawTitle,
      artworkUrl: item.posterUrl,
      ...(runtimeSeconds ? { durationSeconds: runtimeSeconds } : {}),
      ...(alternates.length > 0 ? { altUrls: alternates } : {}),
    });
    if (isResumable(saved)) store.seek(saved!.positionSeconds);
  }

  return (
    <div className="page page--library">
      <Section title="Movies">
        <div className="library-toolbar">
          <input
            className="library-search"
            type="search"
            placeholder="Search movies…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search movies"
          />
          <select className="library-select" value={sort} onChange={(event) => setSort(event.target.value as SortKey)} aria-label="Sort movies">
            {popularOrder && <option value="popular">Most popular</option>}
            <option value="az">A–Z</option>
            <option value="year">Newest year</option>
            <option value="recent">Recently added</option>
          </select>
          <select className="library-select" value={genre} onChange={(event) => setGenre(event.target.value)} aria-label="Filter by genre">
            <option value="all">All genres</option>
            <option value={FAVORITES_CATEGORY}>★ Favorites</option>
            {orderedGenres.newest.map((g) => (
              <option key={g} value={g}>{genreLabel(g)}</option>
            ))}
            {orderedGenres.rest.map((g) => (
              <option key={g} value={g}>{genreLabel(g)}</option>
            ))}
          </select>
          <span className="library-count">{total} movies</span>
        </div>

        {isLoading && (
          <div className="library-grid library-grid--loading"><Spinner /> Loading movies…</div>
        )}
        {isError && isProviderNotConfigured(error) && <ProviderSetupPrompt fullPage />}
        {isError && !isProviderNotConfigured(error) && (
          <ErrorState
            title="Couldn't load movies"
            body="The VOD catalog failed to load. Check the source connection in Settings."
            onRetry={() => window.location.reload()}
          />
        )}
        {!isLoading && !isError && total === 0 && (
          <EmptyState
            icon="🎬"
            title={genre === FAVORITES_CATEGORY ? "No favorite movies yet" : "No movies found"}
            body={
              genre === FAVORITES_CATEGORY
                ? "Press ★ on any movie — in search or here — and it will show up under Favorites."
                : query
                  ? "Try a different search."
                  : "Your source has no movies."
            }
          />
        )}
        {!isLoading && !isError && total > 0 && (
          <div className="library-grid">
            {total > DISPLAY_CAP && (
              <p className="library-cap-note">Showing {DISPLAY_CAP} of {total} — use search or a genre to narrow.</p>
            )}
            {shown.map((movie) => (
              <PosterTile
                key={movie.id}
                movie={movie}
                resumable={isResumable(useProgressStore.getState().get(movie.id))}
                onOpen={openTitle}
              />
            ))}
          </div>
        )}
      </Section>

      <TitleDetail
        // A new panel for each step, so each has its own Back entry: the next
        // panel takes over the closing one's (packages/ui backStack).
        key={trail.length}
        open={selected !== null}
        onClose={closeDetail}
        onOpenRelated={openRelated}
        title={selected?.rawTitle ?? ""}
        subtitle={fields.subtitle}
        overview={fields.overview}
        posterUrl={fields.posterUrl}
        backdropUrl={fields.backdropUrl}
        rating={fields.rating}
        genres={fields.genres}
        cast={cast}
        director={fields.director}
        recommendations={usesTmdb() ? details.data?.recommendations?.results : open.youMayLike}
        similar={usesTmdb() ? details.data?.similar?.results : open.similar}
        trailerUrl={fields.trailerUrl}
        status={{ kind: "streamable", detail: "In your playlist" }}
        onPlay={selected ? () => void handlePlay(selected) : undefined}
        playLabel={resumable && progress ? `▶ Resume (${formatRemaining(progress)})` : "▶ Play"}
      />
    </div>
  );
}

/**
 * One tile. Two components rather than one, because ~97.6% of a catalog ships
 * its own artwork and those tiles need nothing but an <img>: when every tile
 * carried the artwork recovery below, a 300-tile grid opened 300 intersection
 * observers and subscribed to 300 TMDB queries that would never run.
 */
const PosterTile = memo(function PosterTile({
  movie,
  resumable,
  onOpen,
}: {
  movie: VodItem;
  resumable: boolean;
  onOpen: (movie: VodItem) => void;
}) {
  if (!movie.posterUrl) return <PosterTileNoArt movie={movie} resumable={resumable} onOpen={onOpen} />;
  return <PosterCard movie={movie} resumable={resumable} onOpen={onOpen} artUrl={movie.posterUrl} />;
});

/**
 * The artwork-less minority. ~2.4% of a provider catalog ships no stream_icon,
 * and those titles cluster at the head of an A-Z sort (sports events, PPVs,
 * numeric titles), so the first screen looks far emptier than the catalog
 * actually is. Recover what TMDB can identify: only once scrolled near, and
 * only on a match that clears MATCH_THRESHOLD — matchToTMDB returns null
 * rather than guess, so an unrecognised rip keeps its letter tile instead of
 * borrowing some other film's poster.
 */
function PosterTileNoArt({
  movie,
  resumable,
  onOpen,
}: {
  movie: VodItem;
  resumable: boolean;
  onOpen: (movie: VodItem) => void;
}) {
  const [ref, inView] = useInView<HTMLDivElement>();
  const match = useMovieMatch(movie.rawTitle, movie.tmdbId, inView);
  const artUrl = tmdbImageUrl(match.data.match?.summary.poster_path, "w342");
  return <PosterCard movie={movie} resumable={resumable} onOpen={onOpen} artUrl={artUrl} wrapRef={ref} />;
}

function PosterCard({
  movie,
  resumable,
  onOpen,
  artUrl,
  wrapRef,
}: {
  movie: VodItem;
  resumable: boolean;
  onOpen: (movie: VodItem) => void;
  artUrl?: string;
  wrapRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const fallback = tileLetter(movie.rawTitle, "?");
  return (
    <div className="library-card-wrap" ref={wrapRef}>
      <button
        type="button"
        className="library-card"
        onClick={() => onOpen(movie)}
        aria-label={`${movie.rawTitle}${resumable ? " (resume)" : ""}`}
      >
        {artUrl ? (
          <img className="library-card__art" src={tilePoster(artUrl)} alt={movie.rawTitle} loading="lazy" decoding="async" />
        ) : (
          <span className={cn("library-card__fallback", movie.categoryId)}>{fallback}</span>
        )}
        <span className="library-card__title">{movie.rawTitle}</span>
        {resumable && <span className="library-card__resume">● Resume</span>}
      </button>
      <FavoriteStar kind="movie" ref={movie.id} label={movie.rawTitle} className="fav-star--left" />
    </div>
  );
}
